import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import Layout from 'components/layout/Layout'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import type { SeoProps } from 'components/seo/types'
import { useTranslation } from 'next-i18next/pages'
import InjectionTariff from 'features/injection-tariff/InjectionTariff'
import { cmsLib, metricsLib } from 'lib/index'
import { faqCategoriesSlugs } from 'constants/faq-categories'
import { FAQCategories } from 'features/faq-page/types'
import { InjectionTariffProps } from 'features/injection-tariff/types'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { determineSimulationUsage } from 'features/simulation/utils'
import { COUNTER_TYPES, CUSTOMER_TYPES, ESTIMATED_USAGES, METER_TYPES, USAGE_GRADES } from '@boltenergy-be/shared'
import { useStoreDispatch } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { useRef } from 'react'
import { DEFAULT_INJECTION } from 'features/injection-tariff/components/injection-simulation-form/InjectionSimulationForm.constants'

const InjectionTariffPage = (props: Omit<InjectionTariffProps, 'initStoreForInjectionTariff' | 'setSimulatedInjection'>) => {
  // Redux
  const dispatch = useStoreDispatch()
  const { usage } = useSelectInputGroup(InputGroupKeys.SIMULATION)

  // i18n
  const { t } = useTranslation('injection')

  // Constants
  const SEO: SeoProps = {
    description: t('seo.description'),
    title: t('seo.title')
  }

  // Constants
  const simulatedInjectionRef = useRef<number>(usage?.electricity?.injection?.single || DEFAULT_INJECTION)
  const setSimulatedInjection = (value: number) => {
    simulatedInjectionRef.current = value
  }

  /**
   * Update the store with the given injection value from the local injection simulator and enable custom usage grade with average consumption
   * @param {number} newInjection - The new injection value to set in the store. If not provided, it will use the current simulated injection value.
   */
  const initStoreForInjectionTariff = (newInjection?: number) => {
    const injection = Number.isFinite(newInjection) ? newInjection : simulatedInjectionRef.current

    const { consumption } = ESTIMATED_USAGES[USAGE_GRADES.average][CUSTOMER_TYPES.residential].electricity

    dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.HAS_SOLAR_PANELS, value: true }))
    dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.COUNTER_TYPE, value: COUNTER_TYPES.digital }))
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.METER_TYPE, value: METER_TYPES.single }))
    dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.USAGE,
        value: determineSimulationUsage(USAGE_GRADES.custom, {
          meterType: METER_TYPES.single,
          hasSolarPanels: true,
          counterType: COUNTER_TYPES.digital,
          hasExclNight: false,
          electricity: {
            consumption: {
              single: consumption
            },
            injection: {
              single: injection
            }
          }
        })
      })
    )
  }

  return (
    <Layout
      seo={SEO}
      extraStartSimulationAction={async () => {
        initStoreForInjectionTariff()
      }}
    >
      <InjectionTariff {...props} {...{ initStoreForInjectionTariff, setSimulatedInjection }} />
    </Layout>
  )
}

export const getStaticProps = async ({ locale }) => {
  const [communityMetrics, faqCategories] = await Promise.all([
    metricsLib.getCommunityMetrics(),
    cmsLib.getFaqCategories(faqCategoriesSlugs[FAQCategories.INJECTION_TARIFF], locale) || []
  ])

  return {
    props: {
      communityMetrics,
      faqCategories,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'injection'], i18nConfig))
    }
  }
}

export default InjectionTariffPage
